import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';

export type AccessLevel = 'NONE' | 'VIEW' | 'MANAGE';

// module → access for the current user's role
type PermMap = Record<string, AccessLevel>;

@Injectable({ providedIn: 'root' })
export class PermissionService {
  private perms$ = new BehaviorSubject<PermMap | null>(null);

  constructor(private http: HttpClient, private auth: AuthService) {}

  /** Load permissions for the current user's role from the API */
  load(): Observable<PermMap> {
    const role = this.auth.currentUser?.role;
    if (!role) return of({});

    return this.http
      .get<Record<string, string>>(`${environment.apiUrl}/permissions/${role}`)
      .pipe(
        tap((data: Record<string, string>) => {
          const normalised: PermMap = {};
          for (const [mod, acc] of Object.entries(data)) {
            normalised[mod] = acc.toUpperCase() as AccessLevel;
          }
          this.perms$.next(normalised);
        }),
        catchError(() => {
          this.perms$.next({});
          return of({} as Record<string, string>);
        }),
      ) as unknown as Observable<PermMap>;
  }

  /** Clear permissions (on logout) */
  clear(): void { this.perms$.next(null); }

  /** Observable of current permission map */
  get permissions$(): Observable<PermMap | null> { return this.perms$.asObservable(); }

  /** Synchronous check — returns false if not yet loaded */
  can(module: string, level: AccessLevel = 'VIEW'): boolean {
    // ADMIN always passes, regardless of the DB permission matrix — mirrors the
    // backend's requireModule bypass (auth.middleware).
    if (this.isAdmin) return true;
    const perms = this.perms$.value;
    if (!perms) return false;
    const acc = perms[module] ?? 'NONE';
    if (level === 'VIEW')   return acc === 'VIEW'   || acc === 'MANAGE';
    if (level === 'MANAGE') return acc === 'MANAGE';
    return false;
  }

  /** Returns true if user has any access (VIEW or MANAGE) to the module */
  hasAccess(module: string): boolean {
    return this.can(module, 'VIEW');
  }

  /** Returns access level string for a module */
  getLevel(module: string): AccessLevel {
    return this.perms$.value?.[module] ?? 'NONE';
  }

  /** Admin always sees everything regardless of DB permissions */
  get isAdmin(): boolean { return this.auth.currentUser?.role === 'ADMIN'; }
}
